import { hash, toUnitFloat } from "daedalus-core";

/**
 * A speaker's animated dialogue portrait (P2.22-T37): a strip of cells, one of them the rest cell, and a table of clips,
 * each a run of cells with the milliseconds each is held. Two independent timers drive it: the blink timer, whose gaps
 * are short and whose clips are blinks, and the actions timer, whose gaps are longer and whose clips are the odd look or
 * scratch. A timer that falls due while a clip runs waits for it to end, and an action never repeats the one before it.
 * Which clip and how long a gap are drawn from the shared seeded hash, so a given seed plays the same portrait on every
 * machine. Pure; the page only draws the cell it is given.
 */

/**
 * One clip: the cells it shows in order and the milliseconds each is held.
 */
export interface PortraitClip {
  readonly frames: readonly number[];
  readonly ms: readonly number[];
}

/**
 * A blink timer's choice of clip with its weight.
 */
export interface WeightedClip {
  readonly clip: string;
  readonly weight: number;
}

/**
 * A timer's gap range in milliseconds, both ends inclusive.
 */
export type GapRange = readonly [number, number];

/**
 * The portrait's clip table as `portrait-clips.json` holds it, with the strip's URL once the build has hashed it.
 */
export interface PortraitClips {
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly rest: number;
  readonly clips: Readonly<Record<string, PortraitClip>>;
  readonly blink: { readonly gapMs: GapRange; readonly clips: readonly WeightedClip[] };
  readonly actions: { readonly gapMs: GapRange; readonly clips: readonly string[] };
  readonly url?: string;
}

/**
 * The two timers.
 */
export type PortraitTimer = "blink" | "actions";

const TIMER_SALT: Readonly<Record<PortraitTimer, number>> = { blink: 1, actions: 2 };
const GAP_SALT = 1;
const CLIP_SALT = 2;

/**
 * Reads a non-negative integer.
 *
 * @param value The value.
 * @param where Path for the error.
 * @returns The value.
 * @throws Error when it is not a non-negative integer.
 */
function whole(value: unknown, where: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    throw new Error(`${where} must be a non-negative integer`);
  }
  return value;
}

/**
 * Reads a positive integer.
 *
 * @param value The value.
 * @param where Path for the error.
 * @returns The value.
 * @throws Error when it is not a positive integer.
 */
function size(value: unknown, where: string): number {
  if (whole(value, where) === 0) {
    throw new Error(`${where} must be positive`);
  }
  return value as number;
}

/**
 * Narrows to a plain object.
 *
 * @param value The value.
 * @param where Path for the error.
 * @returns The object.
 * @throws Error when it is not an object.
 */
function record(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${where} must be an object`);
  }
  return value as Record<string, unknown>;
}

/**
 * Narrows to an array.
 *
 * @param value The value.
 * @param where Path for the error.
 * @returns The array.
 * @throws Error when it is not an array.
 */
function list(value: unknown, where: string): readonly unknown[] {
  if (!Array.isArray(value)) {
    throw new Error(`${where} must be an array`);
  }
  return value;
}

/**
 * Validates a gap range.
 *
 * @param value The parsed range.
 * @param where Path for the error.
 * @returns The range.
 * @throws Error when it is not two integers, the first positive and not above the second.
 */
function gap(value: unknown, where: string): GapRange {
  const pair = list(value, where);
  const low = whole(pair[0], `${where}[0]`);
  const high = whole(pair[1], `${where}[1]`);
  if (pair.length !== 2 || low === 0 || low > high) {
    throw new Error(`${where} must be [low, high] with 0 < low <= high`);
  }
  return [low, high];
}

/**
 * Validates a parsed `portrait-clips.json`.
 *
 * @param value The parsed JSON.
 * @param cells The number of cells in the strip, or null to skip the bound; every cell a clip or the rest names is below it.
 * @returns The typed table.
 * @throws Error naming the first field that is malformed, a clip whose frames and holds differ in length or are empty,
 *   a cell outside the strip, a timer naming a clip the table lacks, a blink weight that is not positive, or an
 *   action list of fewer than two clips (one could never avoid repeating itself).
 */
export function validatePortraitClips(value: unknown, cells: number | null = null): PortraitClips {
  const table = record(value, "portrait");
  /**
   * Reads a cell index and checks it lies in the strip.
   *
   * @param item The value.
   * @param where Path for the error.
   * @returns The index.
   * @throws Error when it is not a non-negative integer or lies past the strip.
   */
  const cell = (item: unknown, where: string): number => {
    const index = whole(item, where);
    if (cells !== null && index >= cells) {
      throw new Error(`${where} is cell ${index}, past the strip's ${cells}`);
    }
    return index;
  };
  const clips: Record<string, PortraitClip> = {};
  for (const [name, raw] of Object.entries(record(table["clips"], "portrait.clips"))) {
    const clip = record(raw, `portrait.clips.${name}`);
    const frames = list(clip["frames"], `portrait.clips.${name}.frames`).map((item, at) => cell(item, `portrait.clips.${name}.frames[${at}]`));
    const ms = list(clip["ms"], `portrait.clips.${name}.ms`).map((item, at) => whole(item, `portrait.clips.${name}.ms[${at}]`));
    if (frames.length === 0 || frames.length !== ms.length || ms.some((held) => held === 0)) {
      throw new Error(`portrait.clips.${name} needs one positive hold per cell and at least one cell`);
    }
    clips[name] = { frames, ms };
  }
  /**
   * Reads a clip name and checks the table has that clip.
   *
   * @param name The value.
   * @param where Path for the error.
   * @returns The name.
   * @throws Error when the table has no such clip.
   */
  const known = (name: unknown, where: string): string => {
    if (typeof name !== "string" || clips[name] === undefined) {
      throw new Error(`${where} names a clip the table does not have`);
    }
    return name;
  };
  const blink = record(table["blink"], "portrait.blink");
  const blinkClips = list(blink["clips"], "portrait.blink.clips").map((item, at) => {
    const entry = record(item, `portrait.blink.clips[${at}]`);
    const weight = whole(entry["weight"], `portrait.blink.clips[${at}].weight`);
    if (weight === 0) {
      throw new Error(`portrait.blink.clips[${at}].weight must be positive`);
    }
    return { clip: known(entry["clip"], `portrait.blink.clips[${at}].clip`), weight };
  });
  const actions = record(table["actions"], "portrait.actions");
  const actionClips = list(actions["clips"], "portrait.actions.clips").map((item, at) => known(item, `portrait.actions.clips[${at}]`));
  if (blinkClips.length === 0 || actionClips.length < 2) {
    throw new Error("portrait needs a blink clip and at least two action clips");
  }
  const result: PortraitClips = {
    frameWidth: size(table["frameWidth"], "portrait.frameWidth"),
    frameHeight: size(table["frameHeight"], "portrait.frameHeight"),
    rest: cell(table["rest"], "portrait.rest"),
    clips,
    blink: { gapMs: gap(blink["gapMs"], "portrait.blink.gapMs"), clips: blinkClips },
    actions: { gapMs: gap(actions["gapMs"], "portrait.actions.gapMs"), clips: actionClips },
  };
  const url = table["url"];
  return typeof url === "string" ? { ...result, url } : result;
}

/**
 * The wait before a timer's nth firing.
 *
 * @param table The clip table.
 * @param timer Which timer.
 * @param seed The portrait's seed, an unsigned 32-bit integer.
 * @param count How many times the timer has fired already.
 * @returns Milliseconds in the timer's gap range.
 */
export function portraitGap(table: PortraitClips, timer: PortraitTimer, seed: number, count: number): number {
  const [low, high] = table[timer].gapMs;
  return low + Math.floor(toUnitFloat(hash(seed, TIMER_SALT[timer], GAP_SALT, count)) * (high - low + 1));
}

/**
 * The clip a timer plays on its nth firing: a blink drawn by weight; an action drawn evenly from the actions other than
 * `previous`, so none plays twice in a row.
 *
 * @param table The clip table.
 * @param timer Which timer.
 * @param seed The portrait's seed.
 * @param count How many times the timer has played already.
 * @param previous The action played last, or null.
 * @returns The clip's name.
 */
export function portraitClipFor(table: PortraitClips, timer: PortraitTimer, seed: number, count: number, previous: string | null): string {
  const unit = toUnitFloat(hash(seed, TIMER_SALT[timer], CLIP_SALT, count));
  if (timer === "actions") {
    const pool = table.actions.clips.filter((name) => name !== previous);
    return pool[Math.floor(unit * pool.length)] as string;
  }
  const total = table.blink.clips.reduce((sum, entry) => sum + entry.weight, 0);
  let reach = unit * total;
  for (const entry of table.blink.clips) {
    reach -= entry.weight;
    if (reach < 0) {
      return entry.clip;
    }
  }
  return (table.blink.clips[table.blink.clips.length - 1] as WeightedClip).clip;
}
